import React from 'react';
import { Alert, Modal, ModalFooter, Spinner } from '@/components/common';
import { UserCredentials, UserDTO } from '@/models';

interface UserResetPasswordModalProps {
  isOpen: boolean;
  user: UserDTO | null;
  credentials: UserCredentials | null;
  isLoading: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const UserResetPasswordModal: React.FC<UserResetPasswordModalProps> = ({
  isOpen,
  user,
  credentials,
  isLoading,
  onConfirm,
  onClose,
}) => {
  const displayName =
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || 'this user';

  const showCredentials = credentials !== null;
  const showLoading = isLoading && !showCredentials;
  const showConfirm = !showCredentials && !showLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdropClick={false}
      title="Reset Password"
      footer={
        showCredentials ? (
          <ModalFooter onCancel={onClose} confirmText="Done" onConfirm={onClose} />
        ) : (
          <ModalFooter
            onCancel={onClose}
            onConfirm={onConfirm}
            confirmText="Reset Password"
            confirmVariant="danger"
            isLoading={isLoading}
          />
        )
      }
    >
      {showConfirm && (
        <p className="mb-0">
          Are you sure you want to reset the password for <strong>{displayName}</strong>
          {user?.email ? <> ({user.email})</> : null}? A new temporary password will be generated.
        </p>
      )}

      {showLoading && (
        <div className="d-flex justify-content-center py-4">
          <Spinner />
        </div>
      )}

      {showCredentials && credentials && (
        <div>
          <Alert variant="warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Please save these credentials. The temporary password will not be shown again.
          </Alert>
          <div className="mb-3">
            <label className="form-label fw-bold">Username</label>
            <div className="form-control bg-light">{credentials.username || '-'}</div>
          </div>
          <div className="mb-3">
            <label className="form-label fw-bold">Temporary Password</label>
            <div className="form-control bg-light font-monospace">
              {credentials.temporaryPassword || '-'}
            </div>
          </div>
          {credentials.requiresPasswordChange && (
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              User will be required to change their password on first login.
            </small>
          )}
        </div>
      )}
    </Modal>
  );
};